import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import { CollectionService, type ListCollectionsParams } from '../../services';
import { queryKeys } from '../keys';
import type {
  AddAssetsToCollectionRequest,
  CreateCollectionRequest,
  UpdateCollectionRequest,
} from '../../domain';

export function useCollections(filter?: ListCollectionsParams) {
  return useQuery({
    queryKey: queryKeys.collections(filter),
    queryFn: () => CollectionService.list(filter),
  });
}

export function useCollection(id: string | undefined) {
  return useQuery({
    queryKey: queryKeys.collection(id ?? ''),
    queryFn: () => CollectionService.get(id!),
    enabled: !!id,
  });
}

export function useCreateCollection() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: CreateCollectionRequest) => CollectionService.create(body),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['collections'] });
    },
  });
}

export function useUpdateCollection() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, body }: { id: string; body: UpdateCollectionRequest }) =>
      CollectionService.update(id, body),
    onSuccess: (_data, { id }) => {
      qc.invalidateQueries({ queryKey: ['collections'] });
      qc.invalidateQueries({ queryKey: queryKeys.collection(id) });
    },
  });
}

export function useDeleteCollection() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => CollectionService.delete(id),
    onSuccess: (_data, id) => {
      qc.invalidateQueries({ queryKey: ['collections'] });
      qc.invalidateQueries({ queryKey: queryKeys.collection(id) });
    },
  });
}

export function useAddAssetsToCollection() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, body }: { id: string; body: AddAssetsToCollectionRequest }) =>
      CollectionService.addAssets(id, body),
    onSuccess: (_data, { id }) => {
      qc.invalidateQueries({ queryKey: ['collections'] });
      qc.invalidateQueries({ queryKey: queryKeys.collection(id) });
    },
  });
}

export function useRemoveAssetFromCollection() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, assetId }: { id: string; assetId: string }) =>
      CollectionService.removeAsset(id, assetId),
    onSuccess: (_data, { id }) => {
      qc.invalidateQueries({ queryKey: ['collections'] });
      qc.invalidateQueries({ queryKey: queryKeys.collection(id) });
    },
  });
}

export function useReorderCollectionAssets() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, assetIdsInOrder }: { id: string; assetIdsInOrder: string[] }) =>
      CollectionService.reorder(id, assetIdsInOrder),
    onSuccess: (_data, { id }) => {
      qc.invalidateQueries({ queryKey: queryKeys.collection(id) });
    },
  });
}
